import { useEffect, useState } from "react";
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  Radio,
  RadioGroup,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";
import { useApi } from "../../hooks/useApi";
import { BackfillApplyResult, BackfillPeriod, BackfillPreview } from "../../types/billing";
import { formatUtcDateTime } from "../../utils/billingFormat";

interface Props {
  open: boolean;
  companyId: string;
  billingProfileId: number;
  onClose: () => void;
  onApplied: (result: BackfillApplyResult) => void;
}

export default function BillingBackfillDialog({ open, companyId, billingProfileId, onClose, onApplied }: Props) {
  // Separate instances so `loading` tracks the preview fetch only, not the apply POST.
  const { get, loading } = useApi();
  const { post } = useApi();

  const [period, setPeriod] = useState<BackfillPeriod>("full");
  const [applying, setApplying] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [preview, setPreview] = useState<BackfillPreview | null>(null);

  useEffect(() => {
    if (!open) {
      setPeriod("full");
      setPreview(null);
      setError(null);
      return;
    }
    let cancelled = false;
    (async () => {
      setError(null);
      try {
        const res = await get<BackfillPreview>(
          `/api/companies/${companyId}/billing-profiles/${billingProfileId}/backfill/preview?period=${period}`
        );
        if (!cancelled) setPreview(res);
      } catch (err: any) {
        if (!cancelled) setError(err.message || "Failed to load preview");
      }
    })();
    return () => { cancelled = true; };
  }, [open, companyId, billingProfileId, period, get]);

  const total = preview?.meters.reduce((s, m) => s + m.count, 0) ?? 0;

  const handleApply = async () => {
    setApplying(true);
    setError(null);
    try {
      const res = await post<BackfillApplyResult>(
        `/api/companies/${companyId}/billing-profiles/${billingProfileId}/backfill/apply`,
        { period }
      );
      onApplied(res);
      onClose();
    } catch (err: any) {
      setError(err.message || "Failed to apply backfill");
    } finally {
      setApplying(false);
    }
  };

  return (
    <Dialog open={open} onClose={() => !applying && onClose()} maxWidth="sm" fullWidth>
      <DialogTitle>Backfill unbilled alerts</DialogTitle>
      <DialogContent>
        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

        <FormControl sx={{ mb: 2 }}>
          <RadioGroup
            value={period}
            onChange={(_, value) => setPeriod(value as BackfillPeriod)}
          >
            <FormControlLabel
              value="current_month"
              disabled={applying}
              control={<Radio size="small" />}
              label="Current month"
            />
            <FormControlLabel
              value="full"
              disabled={applying}
              control={<Radio size="small" />}
              label="Full — all unbilled history"
            />
          </RadioGroup>
        </FormControl>

        {loading && (
          <Box sx={{ display: "flex", alignItems: "center", gap: 1, py: 2 }}>
            <CircularProgress size={16} />
            <Typography variant="body2" color="text.secondary">Counting alerts…</Typography>
          </Box>
        )}

        {preview && !loading && (
          <>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              {preview.periodStart ? (
                <>Covers unbilled alerts created since <strong>{formatUtcDateTime(preview.periodStart)}</strong>.</>
              ) : (
                <>Covers <strong>all</strong> unbilled alerts billed through this billing profile.</>
              )}{" "}
              Alerts are normally billed automatically as they arrive; this sweeps up any that weren't.
              Already-billed alerts are never counted twice, so it's safe to re-run. Billing runs in the
              background, so counts may take a minute to clear after you apply.
            </Typography>

            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Meter</TableCell>
                  <TableCell align="right">Alerts</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {preview.meters.map((m) => (
                  <TableRow key={m.meterName}>
                    <TableCell sx={{ fontFamily: "monospace" }}>{m.meterName}</TableCell>
                    <TableCell align="right">{m.count}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>

            {preview.unattributed > 0 && (
              <Alert severity="warning" sx={{ mt: 2 }}>
                A further {preview.unattributed} unbilled {preview.unattributed === 1 ? "alert carries" : "alerts carry"} no
                brand, so no billing profile can claim {preview.unattributed === 1 ? "it" : "them"}. Assign a brand on the
                enrolment to bill {preview.unattributed === 1 ? "it" : "them"}.
              </Alert>
            )}

            {total === 0 && (
              <Alert severity="info" sx={{ mt: 2 }}>
                No alerts in this period — nothing to backfill.
              </Alert>
            )}
          </>
        )}
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={onClose} disabled={applying}>Cancel</Button>
        <Button
          onClick={handleApply}
          variant="contained"
          disabled={applying || loading || !preview || total === 0}
          sx={{ backgroundColor: "#465fff", "&:hover": { backgroundColor: "#3641f5" } }}
        >
          {applying ? "Applying…" : "Apply"}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
